import {
  CTS_WORKFLOW_STATES, CTS_WORKFLOW_LABEL, CTS_IMPLEMENTATION_STATUS_LABEL, CTS_NOT_YET_ASSESSED_LABEL,
  CTS_IMPLEMENTATION_STATUSES, CTS_REVIEW_LABELS, CTS_REVIEW_LABEL_TEXT,
  ctsReviewCadenceWithSource,
} from '@grc/shared';
import type { CtsReviewLabel, CtsWorkflowState } from '@grc/shared';
import type { WorkspaceControl, WorkspaceList } from '@/api/types/cts-workspace.types';

// ⚠ CAPR S3 — THIS MODULE HAS NO CLOCK. Every date it shows or groups by is a tenant-local
//   'YYYY-MM-DD' string the server resolved; nothing here constructs a date, reads the time, or
//   compares timestamps. A filter, a table cell and an exported row read the same forwarded fields,
//   so they cannot disagree — and a permanent source guard (workspace-filters.test.ts) keeps it so.

/** A control with its Domain/Family names carried down — what the table and the CSV both read. */
export type FlatControl = WorkspaceControl & { _domain: string; _family: string };

// ── TC2 — the Controls workspace filter model ────────────────────────────────────────────────
//
// One module for the whole left rail: what the bands ARE, how a control is classified into them,
// and how the counts are produced. Kept out of the page because the CSV export has to mirror the
// active filters exactly — and "mirrors the filters" is only true if both read the same predicate
// rather than two implementations that agree today.

export type FilterKey =
  | `scope:${'in' | 'out'}`
  | `framework:${string}`
  | `posture:${string}`
  | `evidence:${'fresh' | 'stale' | 'none'}`
  | `workflow:${CtsWorkflowState}`
  | `assessed:${string}`
  | `review:${CtsReviewLabel}`
  | 'self_approved:yes';

/**
 * ⚠ A KEY THE RAIL DOES NOT KNOW IS IGNORED, NEVER APPLIED. A selection can outlive the band that
 * produced it (a remembered or shared view from before a band was retired — "Not in 12 months" was),
 * and an unknown key inside a section would otherwise match nothing and silently empty the table.
 * Unknown sections and unknown values within a known section are both dropped.
 */
export function isKnownFilterKey(k: string): k is FilterKey {
  const i = k.indexOf(':');
  if (i < 1) return false;
  const section = k.slice(0, i);
  const value = k.slice(i + 1);
  switch (section) {
    case 'scope': return value === 'in' || value === 'out';
    case 'framework': return value.length > 0;
    case 'posture': return value === 'not_yet_assessed' || (CTS_IMPLEMENTATION_STATUSES as readonly string[]).includes(value);
    case 'evidence': return value === 'fresh' || value === 'stale' || value === 'none';
    case 'workflow': return (CTS_WORKFLOW_STATES as readonly string[]).includes(value);
    case 'assessed': return value === 'never' || /^year:\d{4}$/.test(value);
    case 'review': return (CTS_REVIEW_LABELS as readonly string[]).includes(value);
    case 'self_approved': return value === 'yes';
    default: return false;
  }
}

export interface FilterBand { key: FilterKey; label: string; count: number; tone?: string }
export interface FilterSection { id: string; title: string; caption?: string; bands: FilterBand[] }

/**
 * ⚠ CAPR S2 — POSTURE IS NOW THE DERIVED IMPLEMENTATION STATUS (D-CAPR-STATUS-SET): the status of the
 * control's latest APPROVED decision, read from `implementation_status`. The legacy
 * `assessment_result` axis is deprecated and no longer read here — a ledger approval read through it
 * would show "Not Started" forever. The values are the five stored statuses; "Not Yet Assessed" is
 * derived (no approved decision) and replaces "Not Started", which D-CAPR-STATUS-SET retired.
 *
 * ⓘ The note below is the TC2 history of this vocabulary; its "three distinct facts" still hold, with
 * "Not Yet Assessed" in the place of "Not Started".
 *
 * ⚠ D-TC-POSTURE-VOCABULARY — the five control-posture states.
 *
 * "Implemented" is a property of the CONTROL. "Met" implied met-against-a-standard, which is
 * framework-centric language for a control-centric model: a control is implemented ONCE, and
 * whether that satisfies a given framework requirement is the MAPPING's job, one layer out. The
 * label change is the vocabulary catching up with the architecture.
 *
 * ⚠ THE VALUES ARE UNTOUCHED. `met` / `partially_met` / `not_met` are `cts_assessment.result`
 * CHECK values living in the database; only what the surface CALLS them moves. Nothing about the
 * scoring changed: unknown is still not failure, N/A still leaves the denominator, and an
 * unassessed control still reads "Not Started" rather than 0%.
 *
 * ⚠ AND THE THREE NEGATIVE-LOOKING STATES REMAIN THREE DISTINCT FACTS:
 *   · Not Started    — nobody has assessed it (no measurement exists)
 *   · Not Implemented — assessed, and it is not in place (a measurement, and a bad one)
 *   · Not Applicable  — deliberately excluded from scoring
 * Collapsing any pair would destroy exactly what TC1's schema was shaped to keep apart.
 */
export const POSTURE_BANDS = [
  { value: 'implemented', label: CTS_IMPLEMENTATION_STATUS_LABEL.implemented, tone: 'met' },
  { value: 'partially_implemented', label: CTS_IMPLEMENTATION_STATUS_LABEL.partially_implemented, tone: 'partial' },
  { value: 'not_implemented', label: CTS_IMPLEMENTATION_STATUS_LABEL.not_implemented, tone: 'not-met' },
  { value: 'unknown', label: CTS_IMPLEMENTATION_STATUS_LABEL.unknown, tone: 'none' },
  { value: 'not_yet_assessed', label: CTS_NOT_YET_ASSESSED_LABEL, tone: 'none' },
  { value: 'not_applicable', label: CTS_IMPLEMENTATION_STATUS_LABEL.not_applicable, tone: 'none' },
] as const;

/**
 * A control's posture band. ⚠ No approved decision is `not_yet_assessed` — never `unknown` (an ASSESSED
 * indeterminate outcome), never a zero.
 */
export function postureOf(c: WorkspaceControl): string {
  return c.implementation_status ?? 'not_yet_assessed';
}

/**
 * ⚠ The RULED workflow state for a control, derived from its latest open assignment.
 *
 * `null` from the api means no open assignment exists, which IS the ruled state "Unassigned" — a
 * DERIVATION, not a stored value. V1040 admits `unassigned` in the CHECK for vocabulary
 * completeness and asserts that nothing writes it: a row asserting no-assignment-exists would be
 * incoherent, not merely duplicative.
 *
 * ⚠ The TC2 compatibility map is GONE, because V1040 migrated the column and the database now
 * emits these names directly. A translation layer that no longer translates is a place for a
 * future value to be silently absorbed — so an unrecognised value returns null and is counted as
 * unmapped rather than folded into a band it does not belong to.
 */
export function workflowOf(c: WorkspaceControl): CtsWorkflowState | null {
  if (c.workflow_status === null) return 'unassigned';
  return (CTS_WORKFLOW_STATES as readonly string[]).includes(c.workflow_status)
    ? c.workflow_status as CtsWorkflowState
    : null;
}

export function evidenceOf(c: WorkspaceControl): 'fresh' | 'stale' | 'none' {
  if (c.evidence_count === 0) return 'none';
  return c.evidence_stale > 0 ? 'stale' : 'fresh';
}

/** In scope = anything not explicitly ruled out. Undetermined is IN — it has not been excluded. */
export function scopeOf(c: WorkspaceControl): 'in' | 'out' {
  return c.applicability === 'not_applicable' ? 'out' : 'in';
}

/**
 * Assessment recency band.
 *
 * ⚠ CAPR S3 — THE FIXED 12-MONTH "NOT IN 12 MONTHS" BAND IS RETIRED. It was a stand-in for a review
 * schedule that did not exist; the review cadence now answers "is this overdue?" (the Review section,
 * from the server's resolver), and two answers to one question would disagree for any control on a
 * cadence other than annual. Every assessed control now lands in the band for the YEAR of its latest
 * approval — the tenant-local date the server sent — so none falls into a band that no longer exists.
 */
export function assessedOf(c: WorkspaceControl): string {
  // ⚠ CAPR S2 — "assessed" means an APPROVED decision: its date, never the deprecated legacy row's.
  if (c.last_approved_date === null) return 'never';
  return `year:${c.last_approved_date.slice(0, 4)}`;
}

// ── REVIEW PREDICATES — bucket on the server's fields, and nothing else ─────────────────────────
// ⚠ These two functions are the whole client path of the review:* and self_approved filters. They
//   compare ONLY the forwarded label and flag: no date is built, no clock is read, no timestamp is
//   compared. The source guard in workspace-filters.test.ts holds this region to that.
export function reviewOf(c: WorkspaceControl): CtsReviewLabel {
  return c.review_label;
}
export function selfApprovedOf(c: WorkspaceControl): 'yes' | null {
  return c.self_approved === true ? 'yes' : null;
}
// ── END REVIEW PREDICATES ─────────────────────────────────────────────────────────────────────────

/**
 * Flatten, carrying the group names down onto each row.
 *
 * ⚠ The grouping is structure for the EYE; the CSV and the filters need it as DATA. Decorating
 * here keeps one flattening in the module rather than each consumer re-walking the tree and
 * disagreeing about what a row's family is.
 */
export function allControls(data: WorkspaceList): FlatControl[] {
  return data.domains.flatMap((d) => d.families.flatMap((f) =>
    f.controls.map((c) => ({ ...c, _domain: d.name, _family: f.name }))));
}

/** Does one control satisfy the active selection? Bands within a section are OR, sections are AND. */
export function matches(c: WorkspaceControl, active: Set<FilterKey>): boolean {
  const groups: Record<string, string[]> = {};
  for (const k of active) {
    if (!isKnownFilterKey(k)) continue;   // a stale or foreign key degrades quietly — see isKnownFilterKey
    const [section] = k.split(':');
    (groups[section] ??= []).push(k);
  }
  if (Object.keys(groups).length === 0) return true;
  const has = (section: string, keys: string[]) =>
    !groups[section] || groups[section].some((k) => keys.includes(k));

  const wf = workflowOf(c);
  return has('scope', [`scope:${scopeOf(c)}`])
    && has('framework', c.mappings
      .map((m) => m.framework_reference)
      .filter((r): r is string => r !== null)
      .map((r) => `framework:${r}`))
    && has('posture', [`posture:${postureOf(c)}`])
    && has('evidence', [`evidence:${evidenceOf(c)}`])
    && has('workflow', wf ? [`workflow:${wf}`] : [])
    && has('assessed', [`assessed:${assessedOf(c)}`])
    && has('review', [`review:${reviewOf(c)}`])
    && has('self_approved', selfApprovedOf(c) ? ['self_approved:yes'] : []);
}

/**
 * Build the rail.
 *
 * ⚠ THE WORKFLOW SECTION RENDERS ALL EIGHT RULED STATES, ALWAYS — including the three the database
 * cannot yet produce (Completed, Reopened, and Submitted-for-Review where no work has reached it).
 * They show a ZERO, which is the honest middle: hiding them would misrepresent the vocabulary as
 * five states, and fabricating counts would be a lie. Zero-count also means the rail does not
 * CHANGE SHAPE when TC4 lands — the states simply start being populated, so there is no UX churn
 * at the migration.
 *
 * Every other section is built from the data, because those bands describe what is there rather
 * than a fixed vocabulary — an empty posture band is noise, an absent workflow state is a claim.
 */
export function buildSections(data: WorkspaceList): FilterSection[] {
  const controls = allControls(data);
  const tally = <T extends string>(f: (c: WorkspaceControl) => T | null) => {
    const m = new Map<T, number>();
    for (const c of controls) { const v = f(c); if (v !== null) m.set(v, (m.get(v) ?? 0) + 1); }
    return m;
  };

  const scope = tally(scopeOf);
  const posture = tally(postureOf);
  const evidence = tally(evidenceOf);
  const workflow = tally(workflowOf);
  const assessed = tally(assessedOf);
  const review = tally(reviewOf);
  const selfApproved = tally(selfApprovedOf);

  const fwCount = new Map<string, { name: string; n: number }>();
  for (const c of controls) {
    const seen = new Set<string>();
    for (const m of c.mappings) {
      if (m.framework_reference === null || seen.has(m.framework_reference)) continue;
      seen.add(m.framework_reference);
      const cur = fwCount.get(m.framework_reference);
      fwCount.set(m.framework_reference, { name: m.framework_name ?? m.framework_reference, n: (cur?.n ?? 0) + 1 });
    }
  }

  const years = [...assessed.keys()].filter((k) => k.startsWith('year:'))
    .sort().reverse();

  const sections: FilterSection[] = [
    {
      id: 'scope', title: 'Scope',
      bands: [
        { key: 'scope:in', label: 'In scope', count: scope.get('in') ?? 0 },
        { key: 'scope:out', label: 'Out of scope', count: scope.get('out') ?? 0 },
      ],
    },
    {
      id: 'framework', title: 'Adopted frameworks', caption: 'Active',
      bands: [...fwCount.entries()].map(([ref, v]) => ({
        key: `framework:${ref}` as FilterKey, label: v.name, count: v.n,
      })),
    },
    {
      id: 'posture', title: 'Posture',
      bands: POSTURE_BANDS.map((b) => ({
        key: `posture:${b.value}` as FilterKey, label: b.label,
        count: posture.get(b.value) ?? 0, tone: b.tone,
      })).filter((b) => b.count > 0),
    },
    {
      id: 'evidence', title: 'Evidence', caption: 'Computed',
      bands: ([['fresh', 'Fresh evidence'], ['stale', 'Stale evidence'], ['none', 'No evidence']] as const)
        .map(([v, label]) => ({ key: `evidence:${v}` as FilterKey, label, count: evidence.get(v) ?? 0 }))
        .filter((b) => b.count > 0),
    },
    {
      id: 'workflow', title: 'Workflow', caption: 'Assignment',
      // ⚠ The FULL ruled vocabulary, zero-counts included. See the note above.
      bands: CTS_WORKFLOW_STATES.map((st) => ({
        key: `workflow:${st}` as FilterKey,
        label: CTS_WORKFLOW_LABEL[st],
        count: workflow.get(st) ?? 0,
        tone: st,
      })),
    },
    {
      id: 'assessed', title: 'Assessed', caption: 'Recency',
      bands: [
        ...years.map((y) => ({
          key: `assessed:${y}` as FilterKey, label: `In ${y.slice(5)}`, count: assessed.get(y) ?? 0,
        })),
        { key: 'assessed:never' as FilterKey, label: 'Never assessed', count: assessed.get('never') ?? 0 },
      ].filter((b) => b.count > 0),
    },
    {
      // ⚠ CAPR S3 — the review label, every value always shown (a zero is a fact: "nothing overdue"
      //   is exactly what a reviewer wants to read), in the resolver's own order.
      id: 'review', title: 'Review', caption: 'Schedule',
      bands: CTS_REVIEW_LABELS.map((l) => ({
        key: `review:${l}` as FilterKey, label: CTS_REVIEW_LABEL_TEXT[l], count: review.get(l) ?? 0, tone: l,
      })),
    },
    {
      id: 'self_approved', title: 'Approval', caption: 'Current decision',
      bands: [{ key: 'self_approved:yes' as FilterKey, label: 'Self-approved', count: selfApproved.get('yes') ?? 0 }],
    },
  ];
  return sections.filter((s) => s.bands.length > 0);
}

// ── CSV ───────────────────────────────────────────────────────────────────────────────────────

const csvCell = (v: string | number | null): string => {
  const s = v === null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * ⚠ FILTER-AWARE BY CONSTRUCTION, not by convention. The export takes the SAME rows the table is
 * showing rather than re-deriving them, so an export can never disagree with the screen it was
 * taken from — the failure mode where a tenant sends an auditor a file containing rows they had
 * filtered out cannot occur here.
 */
export function toCsv(rows: FlatControl[]): string {
  const head = [
    'Control', 'Title', 'Domain', 'Family', 'Frameworks', 'External references',
    // ⚠ 'Owner' STAYS HERE, alone among the rendered labels. A CSV column header is copy AND a
    //   de-facto interface: a tenant with a script keyed to it breaks silently on a rename, and an
    //   export gives no deprecation path. Held with the projection alias under
    //   B-RACI-CONTRACT-FIELD-RENAME rather than moved with the in-app labels.
    'Owner', 'Posture', 'Workflow', 'Last assessed', 'Evidence', 'Fresh', 'Stale',
    // ⚠ CAPR S3 — APPENDED, never inserted: a script reading the columns above by position keeps
    //   working. The dates are the server's tenant-local strings, written as sent.
    'Review', 'Next review', 'Review cadence', 'Assessment in progress', 'Self-approved',
  ];
  const body = rows.map((c) => {
    const wf = workflowOf(c);
    return [
      c.display_code, c.title, c._domain, c._family,
      c.mappings.map((m) => m.framework_name).filter(Boolean).join('; '),
      c.mappings.map((m) => m.external_ref_id).filter(Boolean).join('; '),
      c.owner_name,
      POSTURE_BANDS.find((b) => b.value === postureOf(c))?.label ?? postureOf(c),
      // An unmapped workflow value exports as itself, exactly as the UI renders it.
      wf ? CTS_WORKFLOW_LABEL[wf] : (c.workflow_status ?? ''),
      // ⚠ CAPR S3 — the tenant-local approval date the server resolved, so "Last assessed" and
      //   "Next review" are dates on the same calendar (the UTC slice could sit a day apart).
      c.last_approved_date ?? 'Never assessed',
      c.evidence_count, c.evidence_fresh, c.evidence_stale,
      CTS_REVIEW_LABEL_TEXT[c.review_label] ?? c.review_label,
      c.next_review_date ?? '',
      ctsReviewCadenceWithSource(c.effective_cadence, c.cadence_source),
      c.in_progress ? 'Yes' : 'No',
      c.self_approved === true ? 'Yes' : c.self_approved === false ? 'No' : '',
    ].map(csvCell).join(',');
  });
  return [head.map(csvCell).join(','), ...body].join('\r\n');
}
